import React, { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import RightSideModal from "../Modal/right-side-modal";
import InputField from "../form-fields/InputField";
import SelectField from "../form-fields/SelectField";
import {
  EntityType,
  // PropertyOwnerTypesEnum,
  // PropertyOwnershipDetailVM,
  PropertyService,
  TenantService,
  UserBaseVM,
  AssistanceProviderVM,
  RentAssistanceProviderTypesEnum,
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import Card from "../card";
import PhoneInput from "../form-fields/PhoneInput";
import EmailInput from "../form-fields/EmailInput";
import CurrencyInput from "../form-fields/CurrencyInput";

enum PropertyOwnerTypesEnum {
  Individual = 1,
  Organization = 2,
}

interface PropertyOwnershipDetailVM {
  ownerTypeId?: number;
  ownerName?: string;
  yearBuilt?: number;
  purchasePrice?: number;
  purchaseDate?: string | Date;
}

interface OwnershipFormProps {
  isOpen: boolean;
  onClose: () => void;
  entityId: number;
  entityTypeId: number;
  defaultValues?:
    | PropertyOwnershipDetailVM
    | UserBaseVM
    | AssistanceProviderVM
    | null;
  defaultDetailType?: string | null; // For tenant case: "1" for Emergency Contact, "2" for Assistance Provider
  refreshData: () => void;
  hasEmergencyContact?: boolean;
  hasAssistanceProvider?: boolean;
}

interface FormValues
  extends UserBaseVM, AssistanceProviderVM, PropertyOwnershipDetailVM {
  detailType?: string; // Only for frontend, not sent to backend
}

const ManageOwnershipDetailsForm: React.FC<OwnershipFormProps> = ({
  isOpen,
  onClose,
  entityId,
  entityTypeId,
  defaultValues,
  defaultDetailType,
  refreshData,
  hasEmergencyContact,
  hasAssistanceProvider,
}) => {
  const isProperty = entityTypeId === EntityType.Property;
  const isCurrentTenant = entityTypeId === EntityType.Tenant;

  const methods = useForm<FormValues>({
    defaultValues: isProperty
      ? {
          ownerName: "",
          ownerTypeId: PropertyOwnerTypesEnum.Individual,
          yearBuilt: undefined,
          purchasePrice: undefined,
          purchaseDate: undefined,
        }
      : {
          detailType: defaultDetailType || (hasEmergencyContact ? "2" : "1"),
          firstName: "",
          lastName: "",
          email: "",
          phoneNumber: "",
          providerTypeId: RentAssistanceProviderTypesEnum.Section8,
          assistanceAmount: undefined,
        },
  });

  const { reset, watch } = methods;
  const detailType = watch("detailType");

  useEffect(() => {
    if (defaultValues) {
      if (isProperty) {
        reset({
          ownerName:
            (defaultValues as PropertyOwnershipDetailVM).ownerName || "",
          ownerTypeId:
            Number((defaultValues as PropertyOwnershipDetailVM).ownerTypeId) ||
            PropertyOwnerTypesEnum.Individual,
          yearBuilt:
            (defaultValues as PropertyOwnershipDetailVM).yearBuilt || undefined,
          purchasePrice:
            (defaultValues as PropertyOwnershipDetailVM).purchasePrice ||
            undefined,
          purchaseDate:
            defaultValues &&
            "purchaseDate" in defaultValues &&
            defaultValues.purchaseDate
              ? new Date(defaultValues.purchaseDate as any)
                  .toISOString()
                  .substring(0, 10)
              : undefined,
        });
      } else {
        reset({
          detailType:
            defaultDetailType ||
            ((defaultValues as AssistanceProviderVM).assistanceAmount
              ? "2"
              : "1"),
          firstName:
            (defaultValues as UserBaseVM | AssistanceProviderVM).firstName ||
            "",
          lastName:
            (defaultValues as UserBaseVM | AssistanceProviderVM).lastName || "",
          email:
            (defaultValues as UserBaseVM | AssistanceProviderVM).email || "",
          phoneNumber:
            (defaultValues as UserBaseVM | AssistanceProviderVM).phoneNumber ||
            "",
          providerTypeId:
            (defaultValues as AssistanceProviderVM).providerTypeId ||
            RentAssistanceProviderTypesEnum.Section8,
          assistanceAmount:
            (defaultValues as AssistanceProviderVM).assistanceAmount ||
            undefined,
        });
      }
    } else if (isCurrentTenant) {
      reset({
        detailType: defaultDetailType || (hasEmergencyContact ? "2" : "1"),
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        providerTypeId: RentAssistanceProviderTypesEnum.Section8,
        assistanceAmount: undefined,
      });
    }
  }, [
    defaultValues,
    defaultDetailType,
    reset,
    isProperty,
    isCurrentTenant,
    hasEmergencyContact,
  ]);

  const handleClose = () => {
    if (isProperty) {
      reset({
        ownerName: "",
        ownerTypeId: PropertyOwnerTypesEnum.Individual,
        yearBuilt: undefined,
        purchasePrice: undefined,
        purchaseDate: undefined,
      });
    } else {
      reset({
        detailType: defaultDetailType || (hasEmergencyContact ? "2" : "1"),
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        providerTypeId: RentAssistanceProviderTypesEnum.Section8,
        assistanceAmount: undefined,
      });
    }
    onClose();
  };

  const onSubmit = async (data: FormValues) => {
    try {
      let result;

      if (isProperty) {
        const propertyData: PropertyOwnershipDetailVM = {
          ...data,
          ownerTypeId: data.ownerTypeId ? Number(data.ownerTypeId) : undefined,
          purchaseDate: data.purchaseDate
            ? new Date(data.purchaseDate).toISOString()
            : undefined,
        };

        result = await apiHelper(
          PropertyService.putApiPropertyOwnershipDetails,
          {
            successMessage: "Ownership details saved",
            showNotification: true,
          },
          entityId,
          propertyData,
        );
      } else {
        const { detailType, ...payload } = data;
        if (data.detailType == "1") {
          result = await apiHelper(
            TenantService.putApiTenantEmergencyContact,
            {
              successMessage: "Emergency contact saved",
              showNotification: true,
            },
            entityId,
            payload as UserBaseVM,
          );
        } else {
          result = await apiHelper(
            TenantService.putApiTenantAssistanceProvider,
            {
              successMessage: "Assistance provider saved",
              showNotification: true,
            },
            entityId,
            payload as AssistanceProviderVM,
          );
        }
      }

      if (result && result.data) {
        refreshData();
        handleClose();
      } else {
        console.warn("Request failed, keeping form open for corrections.");
      }
    } catch (error) {
      console.error("Error saving details", error);
    }
  };

  const detailTypeOptions = [
    !hasEmergencyContact && { Id: 1, Name: "Emergency Contact Info" },
    !hasAssistanceProvider && { Id: 2, Name: "Assistance Provider" },
  ].filter(Boolean);

  return (
    <RightSideModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Details"
      buttonlabel="Save"
      formId="ownershipForm"
      isDirty={methods.formState.isDirty}
      maxWidth={550}
    >
      <Card className="no-bg">
        <FormProvider {...methods}>
          <form id="ownershipForm" onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="row">
              {isProperty ? (
                <>
                  <div className="col-md-6">
                    <SelectField
                      name="ownerTypeId"
                      label="Owner Type"
                      required
                      requiredMessage="Owner Type is required"
                      isAsterisk
                      options={[
                        {
                          Id: PropertyOwnerTypesEnum.Individual,
                          Name: "Individual",
                        },
                        {
                          Id: PropertyOwnerTypesEnum.Organization,
                          Name: "Organization",
                        },
                      ]}
                      placeholderText="Select owner type"
                    />
                  </div>
                  <div className="col-md-6">
                    <InputField
                      name="ownerName"
                      label="Owner Name"
                      required
                      requiredMessage="Owner name is required"
                      isAsterisk
                      placeholder="Enter owner name"
                      onlyAlphabets={true}
                      validation={{
                        maxLength: {
                          value: 40,
                          message: "Owner name cannot exceed 40 characters",
                        },
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <InputField
                      name="yearBuilt"
                      label="Year Built"
                      type="number"
                      required
                      placeholder="Enter year built"
                      requiredMessage="Year built is required"
                      validation={{
                        pattern: {
                          value: /^[0-9]+$/,
                          message: "Must contain only numbers",
                        },
                        validate: (value) => {
                          if (!value) return true;
                          const currentYear = new Date().getFullYear();
                          const year = parseInt(value, 10);
                          return (
                            year <= currentYear ||
                            "Year built cannot be greater than current year"
                          );
                        },
                      }}
                      max={new Date().getFullYear()}
                    />
                  </div>
                  <div className="col-md-6">
                    <CurrencyInput
                      name="purchasePrice"
                      label="Purchase Price"
                      required
                      placeholder="Enter purchase price"
                      requiredMessage="Purchase price is required"
                      allowDecimals={false}
                    />
                  </div>
                  <div className="col-md-6">
                    <InputField
                      name="purchaseDate"
                      label="Purchase Date"
                      type="date"
                      required
                      placeholder="Enter purchase date"
                      requiredMessage="Purchase date is required"
                    />
                  </div>
                </>
              ) : (
                <>
                  {detailTypeOptions.length > 0 && (
                    <div className="col-md-12">
                      <SelectField
                        name="detailType"
                        label="Detail Type"
                        required
                        requiredMessage="Detail Type is required"
                        isAsterisk
                        options={detailTypeOptions}
                        placeholderText="Select detail type"
                      />
                    </div>
                  )}

                  {detailType == "2" && (
                    <>
                      <div className="col-md-12">
                        <CurrencyInput
                          name="assistanceAmount"
                          label="Assisted Amount"
                          required
                          placeholder="Enter assisted amount"
                          requiredMessage="Assisted Amount is required"
                        />
                      </div>
                      <div className="col-md-6 d-none">
                        <SelectField
                          name="providerTypeId"
                          label="Provider Type"
                          required
                          requiredMessage="Provider Type is required"
                          isAsterisk
                          options={[
                            {
                              Id: RentAssistanceProviderTypesEnum.Section8,
                              Name: "Section 8",
                            },
                          ]}
                          placeholderText="Select provider type"
                        />
                      </div>
                      <h4 className="social-worker-details">
                        SOCIAL WORKER DETAILS
                      </h4>
                    </>
                  )}

                  <div className="col-md-6">
                    <InputField
                      name="firstName"
                      label="First Name"
                      required
                      requiredMessage="First Name is required"
                      isAsterisk
                      placeholder="Enter first name"
                      onlyAlphabets={true}
                      validation={{
                        maxLength: {
                          value: 20,
                          message: "First name cannot exceed 20 characters",
                        },
                        minLength: {
                          value: 2,
                          message:
                            "First name must be at least 2 characters long",
                        },
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <InputField
                      name="lastName"
                      label="Last Name"
                      required
                      requiredMessage="Last Name is required"
                      isAsterisk
                      placeholder="Enter last name"
                      onlyAlphabets={true}
                      validation={{
                        maxLength: {
                          value: 20,
                          message: "Last name cannot exceed 20 characters",
                        },
                        minLength: {
                          value: 2,
                          message:
                            "Last name must be at least 2 characters long",
                        },
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <EmailInput
                      name="email"
                      label="Email Address"
                      required
                      requiredMessage="Email is required"
                      placeholder="Enter Email Address"
                    />
                  </div>
                  <div className="col-md-6">
                    <PhoneInput
                      name="phoneNumber"
                      label="Phone Number"
                      required
                      requiredMessage="Phone Number is required"
                      placeholder="Enter phone number"
                    />
                  </div>
                </>
              )}
            </div>
          </form>
        </FormProvider>
      </Card>
    </RightSideModal>
  );
};

export default ManageOwnershipDetailsForm;
